import React, { useEffect, useState } from 'react';
import { Button, Text, View } from "../Components/Themed";
import { MyOrdersHelpers } from '../MarketComponents/components/MyOrderList/api';
import type { AthleteProps, EventProps, LeagueProps, MarketProps, MatchProps, MyPlayerProps, OrderProps, PlayerFilterProps, PlayerPremiumItem, PremiumItemProps, PublicPlayerProps, TeamProps, TournamentProps } from '../types';
import { BetMatchApi } from './api';
import { ActivityIndicator, FlatList, StyleSheet, TouchableWithoutFeedback } from 'react-native';
import BetMatchCardSmall from './components/BetMatchCardSmall';
import { SocialOrderHelpers } from '../SocialComponents/api';
import moment from 'moment-mini';
import { useColors } from '../constants/useColors';
import BetMatchCard from './components/BetMatchCard';
import Pagination from '../Components/Pagination';

type BetMatchProps = {
    player_filter_id:string,
    style?:any,
    mode:'desktop'|'mobile',
    me?:MyPlayerProps,
    insets?:{ top:number, bottom:number, right?:number, left?:number }
    show_grades?:boolean,
    show_external_prices?:boolean,
    onViewPremium?:() => void,
    callback_order?: { result: 'success'|'cancel', order_id:string },
    onPurchasePremium:(ppi:PlayerPremiumItem) => void,
    player_premium_items?:PlayerPremiumItem[],
    premium_items?:PremiumItemProps[],
    onViewPlayer:(player:PublicPlayerProps) => void,
    onSelectGrade:(order:OrderProps) => void,
    onOrder:(order:OrderProps) => void
}
const sections = ['filter','orders', 'bet']
const BetMatch = ({ me, player_filter_id, mode, insets, player_premium_items, premium_items, callback_order, onViewPlayer, onSelectGrade, onOrder, onPurchasePremium, onViewPremium }:BetMatchProps) => {
    const Colors = useColors();
    
    const [ viewing_order_index, setViewingOrderIndex ] = useState<number>(-1);
    const [ static_data, setStaticData ] = useState<{
        static_loading:boolean,
        leagues:LeagueProps[],
        markets:MarketProps[],
    }>({
        static_loading: false, leagues:[], markets:[]
    });
    const { leagues } = static_data;
    const [ filter_data, setFilterData ] = useState<{
        loading:boolean,
        player_filter?:PlayerFilterProps,
        action_loading:boolean,
        orders:OrderProps[],
    }>({
        loading:false, orders:[], action_loading:false
    });
    const { loading, player_filter, orders, action_loading } = filter_data;
    const viewing_order = orders[viewing_order_index];
    const [ contest_data, setContestData ] = useState<{
        contest_loading:boolean,
        players:PublicPlayerProps[],
        events:EventProps[],
        tournaments:TournamentProps[],
        matches:MatchProps[],
        teams:TeamProps[],
        athletes:AthleteProps[]
    }>({
        contest_loading: false, players: [], events: [], tournaments: [], matches: [], teams:[], athletes: []
    });
    const { contest_loading, players, events, tournaments, matches, teams, athletes } = contest_data;
    
    useEffect(() => {
        BetMatchApi.setEnvironment()
        runFilter(player_filter_id);
        getStaticData();
    },[player_filter_id]);

    useEffect(() => {
        if(!callback_order){ return }
        //filter out the order!
        if(callback_order.result == 'success'){
            setFilterData({ ...filter_data, orders: orders.filter(o => o.order_id != callback_order.order_id), action_loading: false })
        } else {
            setFilterData({ ...filter_data, action_loading: false })
        }
    },[callback_order])

    const runFilter = async(id:string) => {
        setFilterData({ ...filter_data, loading: true });
        const pf = await BetMatchApi.getPlayerFilterById(id);
        if(!pf){ return }
        const filter_orders = await BetMatchApi.runPlayerFilter(id);
        setFilterData({
            ...filter_data,
            orders: filter_orders,
            player_filter: pf,
            loading: false
        });
        getContestData(filter_orders);
    }

    const getStaticData = async() => {
        setStaticData({ ...static_data, static_loading:true });
        const lgs = await BetMatchApi.getLeagues();
        const makets = await BetMatchApi.getMarkets();
        setStaticData({ 
            ...static_data,
            static_loading:true,
            leagues: lgs,
            markets: makets
        });
    }

    const getContestData = async(orders:OrderProps[]) => {
        setContestData({ ...contest_data, contest_loading:true });
        const { event_ids, tournament_ids, match_ids, athlete_ids, team_ids, player_ids } = MyOrdersHelpers.getContestsFromOrders(orders);
        const plyers = await BetMatchApi.getPlayersByPlayerIds(player_ids);
        const evs = await BetMatchApi.getEventsByEventIds(event_ids);
        const tourns = await BetMatchApi.getTournamentsByTournamentIds(tournament_ids);
        const matchs = await BetMatchApi.getMatchesByMatchIds(match_ids);
        const aths = await BetMatchApi.getAthletesByIds(athlete_ids);
        const tms = await BetMatchApi.getTeamsByIds(team_ids);
        setContestData({
            ...contest_data,
            contest_loading: false,
            players: plyers,
            events: evs,
            tournaments: tourns,
            matches: matchs,
            teams: tms,
            athletes: aths
        })
    }

    const handleReject = async(order:OrderProps) => {
        setFilterData({ ...filter_data, action_loading: true });
        await BetMatchApi.saveBetMatchResponse({
            order_id: order.order_id,
            response:'decline',
            be_type: 'market',
            bet_match_response_id:'',
            player_id: '',
            create_datetime:'', last_update_datetime:''
        })
        setTimeout(() => {
            setFilterData({
                ...filter_data,
                orders: orders.filter(o => o.order_id != order.order_id)
            })
        }, 250);
    }


    const renderOrders = (data:{ item:OrderProps, index:number }) => {
        const player = players.find(p => p.player_id == data.item.player_id);
        if(!player){ return <></> }
        const { team, athlete, league, title, time } = SocialOrderHelpers.getContestDetails(data.item, events, tournaments, matches, athletes, teams, leagues)
        return (
            <View transparent key={data.item.order_id.toString()} style={{ width:150, maxWidth:250, flexGrow:1, margin:7 }}>
                <BetMatchCardSmall
                    player={player}
                    team={team}
                    premium_items={premium_items}
                    player_premium_items={player_premium_items}
                    athlete={athlete}
                    contest_title={title}
                    time_detail={time}
                    league={league}
                    order={data.item}
                    onOrder={() => setViewingOrderIndex(data.index)}
                />
            </View>
        )
    }

    const renderSections = (data:{item:string, index:number}) => {
        switch(data.item){
            case 'filter':
                if(!player_filter){ return <></> }
                return (
                    <View type='header' style={{ flexDirection:'row', alignItems:'center', padding:10 }}>
                        <View transparent style={{ flex:1, marginRight:10 }}>
                            <Text theme='h1'>{player_filter.name}</Text>
                            <Text theme='description' style={{ marginTop:3 }}>Created {moment(player_filter.create_datetime).fromNow()}</Text>
                        </View>
                        <View transparent style={{ alignItems:'flex-end' }}>
                            <Text theme='h1'>{orders.length}</Text>
                            <Text theme='description' style={{ marginTop:3 }}>Offers</Text>
                        </View>
                    </View>
                )
            case 'orders':
                return (
                    <View>
                        {loading || contest_loading ?
                        <View transparent style={{ justifyContent:'center', alignItems:'center', padding:50 }}>
                            <ActivityIndicator size='large' color={Colors.text.h1} />
                            <Text theme='h1' color={Colors.text.h1} style={{ marginTop:10 }}>Running Your Filter...</Text>
                        </View>
                        :<></>}
                         <View style={{ flexDirection:'row', flexWrap:'wrap' }}>
                            {orders.map((item, index) => {
                                return renderOrders({ item, index })
                            })}
                        </View>
                    </View>
                )

            default: return <></>
        }
    }
    const viewing_player = players.find(p => p.player_id == viewing_order?.player_id);
    const { team, athlete, league, title, time } = SocialOrderHelpers.getContestDetails(viewing_order, events, tournaments, matches, athletes, teams, leagues)
    return (
        <View style={{ flex:1 }}>
            <FlatList
                data={sections}
                key={'filter_sections'}
                keyExtractor={item => item}
                renderItem={renderSections}
            />
            {viewing_order ?
            <View type='blur' style={{ position:'absolute', top:0, left:0, right:0, bottom:0 }}>
                <TouchableWithoutFeedback disabled={mode == 'desktop' ? false : true} style={{ flex:1 }} onPress={() => setViewingOrderIndex(-1)}>
                    <View type="blur" style={mode=='desktop' ? styles.desktopContainer : { ...styles.viewContainer }}>
                        <TouchableWithoutFeedback disabled={mode == 'desktop' ? false : true}>
                            <View transparent style={mode=='desktop' ?{ width:500, maxHeight:750, borderRadius:22 }:{ flex:1, paddingTop:insets?.top ?? 0, paddingBottom:insets?.bottom ?? 0 }}>
                                {viewing_order ?
                                <View transparent style={{ padding:10, paddingRight:20, paddingLeft:20, flex:1 }}>
                                    <BetMatchCard
                                        player={viewing_player}
                                        order={viewing_order}
                                        team={team}
                                        player_premium_items={player_premium_items}
                                        premium_items={premium_items}
                                        me={me}
                                        player_filter={player_filter}
                                        athlete={athlete}
                                        league={league}
                                        action_loading={action_loading}
                                        time_detail={time}
                                        contest_title={title}
                                        onViewPlayer={onViewPlayer}
                                        onPurchasePremium={onPurchasePremium}
                                        onViewPremium={onViewPremium}
                                        onSelectGrade={onSelectGrade}
                                        onOrder={(order) => {
                                            onOrder(order);
                                        }}
                                        onReject={(order) => handleReject(order)}
                                    />
                                </View>
                                :<></>}
                                <View type='footer' style={{ flexDirection:'row', alignItems:'center', padding:5, paddingBottom: (insets?.bottom ?? 0) + 5, borderRadius:8 }}>
                                    <Button
                                        float
                                        title='X'
                                        title_color={Colors.text.error}
                                        style={{ borderRadius:100, height:35, width:35, padding:0, justifyContent:'center', alignItems:'center' }}
                                        onPress={() => {
                                            if(action_loading){ setFilterData({ ...filter_data, action_loading: false }) }
                                            setViewingOrderIndex(-1)
                                        }}
                                    />
                                    <View transparent style={{ flex:1 }}>
                                        <Pagination
                                            pages={orders.length}
                                            onSelectPage={(page) => setViewingOrderIndex(page)}
                                            onNext={() => setViewingOrderIndex(viewing_order_index + 1)}
                                            onPrevious={() => setViewingOrderIndex(viewing_order_index - 1)}
                                            offset={viewing_order_index}
                                        />
                                    </View>
                                </View>
                            </View>
                        </TouchableWithoutFeedback>
                    </View>
                </TouchableWithoutFeedback>
            </View>
            :<></>}
        </View>
    )
}

const styles = StyleSheet.create({
    modalContainer: { flex:1 },
    viewContainer: { flex:1, justifyContent:'flex-end' },
    desktopContainer:{ flex:1, justifyContent:'center', alignItems:'center' }
})

export default BetMatch